import { Controller, useFormContext } from "react-hook-form";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { FieldProps } from "@/core/components/Field";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { KeyboardEvent, useEffect, useState } from "react";
import { HOTJAR_ATTRIBUTES as extraProps } from "@/core/data";
import { Box, Theme } from "@mui/material";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import {
  getCountryByDomain,
  getLanguageByDomain,
  getTopLevelDomain,
} from "@/core/utils";
import ErrorMessage from "@/core/components/ErrorMessage/ErrorMessage";
import { Countries, Domains, Languages } from "@/core/types/types";
import { useClaimCheck } from "@/core/hooks/useClaimCheck/useClaimCheck";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";

const styles = (theme: Theme, { isError }: { isError: boolean }) => ({
  root: {
    "& div.special-label": {
      display: "block",
      fontSize: "12px",
      color: isError ? theme.palette.error.main : theme.palette.grey.A200,
      padding: theme.spacing(0, 0.5),
      background: "transparent",
      position: "absolute",
      zIndex: 0,
    },
    // Border masking
    "& div.special-label::before, & div.special-label::after": {
      position: "absolute",
      zIndex: -1,
      display: "block",
      width: "100%",
      top: "6px",
      left: 0,
      height: "2px",
      content: `""`,
      background: `${theme.palette.background.default}`,
    },
    "& div.special-label::after": {
      height: "1px",
      background: `${theme.palette.grey[100]}`,
    },
    "& input.telephone": {
      width: "100%",
      border: `1px solid ${
        isError ? theme.palette.error.main : theme.palette.grey.A200
      }`,
    },
    "& input.telephone:hover, & input.telephone:focus": {
      border: "1px solid",
      borderColor: isError
        ? theme.palette.error.main
        : theme.palette.primary.main,
    },
    "& input.telephone:focus": {
      boxShadow: `0 0 0 1px ${
        isError ? theme.palette.error.main : theme.palette.primary.main
      }`,
    },
  },
});

const sanitizeGermanPhoneNumber = (number: string) => {
  let sanitizedNumber = number.replace(/[^\d+]/g, "");

  if (sanitizedNumber.startsWith("+490")) {
    sanitizedNumber = `+49${sanitizedNumber.slice(4)}`;
  }

  const match = sanitizedNumber.match(
    /^(\+?[0-9]{1,3})?([0-9]{1,6})?([0-9]{3,20})$/
  );

  if (match) {
    const countryCode = match[1] ? match[1] + " " : "";
    const areaCode = match[2] ? `(${match[2]}) ` : "";
    const phoneNumber = match[3]
      ? match[3].replace(/(\d{3})(\d{3})/, "$1-$2")
      : "";

    return `${countryCode}${areaCode}${phoneNumber}`;
  }

  return sanitizedNumber;
};

const validatePhoneNumberLength = (value: string, minLength: number) => {
  const numberWithoutSpaces: number = value.match(/\d/g)?.length ?? 0;

  return numberWithoutSpaces >= minLength || false;
};

const Telephone = ({ field, initialValue }: FieldProps) => {
  const { defaultValue } = field;
  const {
    control,
    setValue,
    trigger,
    handleSubmit,
    formState: { errors },
  } = useFormContext();
  const { submitStep } = useClaimCheck();
  const [phone, setPhone] = useState<string>("");
  const initialText = useDefaultValue(initialValue ?? defaultValue);
  const [localization, setLocalization] = useState<{} | null>(null);
  const isError: boolean = !!errors[field.key];
  const errorMessage: string = isError ? `${errors[field.key]?.message}` : "";
  const classes: any = useCustomStyles(styles, { isError });
  const tld: Domains = getTopLevelDomain() as Domains;
  const lang: Languages = getLanguageByDomain(tld);
  const country: Countries = getCountryByDomain(tld);
  const label: string = t(field.label);
  const requiredErrorMessage = t(`validate.${field.key}.required.message`);
  const minLengthErrorMessage = t(`validate.${field.key}.minLength.message`);

  const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const { value } = target;
    const sanitizedValue = sanitizeGermanPhoneNumber(value);
    setPhone(value);
    setValue(field.key, sanitizedValue);
    trigger(field.key);
  };

  const handleEnterKey = async () => {
    const isValid: boolean = await trigger();
    if (isValid) {
      handleSubmit(submitStep)();
    }
    return;
  };

  useEffect(() => {
    // Import localization object dynamically
    const getLocalization = async (lang: string) => {
      try {
        const locale = await import(`react-phone-input-2/lang/${lang}.json`);
        setLocalization(locale);
      } catch (error) {
        console.error("Error occurred while fetching JSON data: ", error);
        setLocalization({});
      }
    };

    getLocalization(lang);
  }, [lang]);

  useEffect(() => {
    if (initialText) {
      setPhone(initialText);
      setValue(field.key, initialText);
    }
  }, [initialText]);

  return localization ? (
    <Box sx={classes.root}>
      <Controller
        name={field.key}
        control={control}
        rules={{
          required: {
            value: field.validate?.required || false,
            message: requiredErrorMessage,
          },
          validate: (value) => {
            const isValid = validatePhoneNumberLength(
              value,
              field.validate?.minLength || 0
            );
            return isValid || minLengthErrorMessage;
          },
        }}
        render={() => (
          <>
            <Box onKeyUp={handleKeyUp}>
              <PhoneInput
                {...extraProps}
                placeholder=""
                localization={localization}
                specialLabel={label}
                inputClass="telephone"
                country={country.toLowerCase()}
                value={phone}
                onEnterKeyPress={handleEnterKey}
                inputProps={{
                  ...extraProps.inputProps,
                  id: field.key,
                  name: field.key,
                }}
              />
            </Box>
            <Box ml={0.75}>
              {isError ? <ErrorMessage text={errorMessage} /> : null}
            </Box>
          </>
        )}
      ></Controller>
    </Box>
  ) : null;
};

export default Telephone;
